import { useMemo, type ReactNode } from 'react'
import {
  NotReady,
  Page,
  TButton,
  UserInlineSelect,
  ComplianceViolationsList,
  CompliancePoliciesList,
} from '@components'
import {
  useCompliancePolicies,
  useComplianceViolationAggregate,
  useComplianceViolations,
  useTranslation,
} from '@front/model'
import { useToastError } from '@hooks'
import { Grid, Group, Paper, Stack, Text } from '@mantine/core'
import { useNavigate, createFileRoute } from '@tanstack/react-router'
import { type UserId, type WithCompliancePolicyId, type WithComplianceViolationId } from '@terros/common'
import { ComplianceGauge } from './ComplianceGauge'

function ComplianceDashboard(): ReactNode {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    data: agg,
    error: aggError,
    isFetching: isAggFetching,
    isLoading: isAggLoading,
    refetch: refetchAgg,
  } = useComplianceViolationAggregate()
  const {
    data: policies,
    error: policiesError,
    isFetching: isPoliciesFetching,
    isLoading: isPoliciesLoading,
    refetch: refetchPolicies,
  } = useCompliancePolicies({ archived: false })
  const {
    data: violations,
    error: violationsError,
    isFetching: isViolationsFetching,
    isLoading: isViolationsLoading,
    refetch: refetchViolations,
  } = useComplianceViolations()
  useToastError(aggError)

  const refetch = (): void => {
    refetchAgg()
    refetchPolicies()
    refetchViolations()
  }

  const isFetching = isAggFetching || isPoliciesFetching || isViolationsFetching

  const activePolicies = useMemo(() => policies?.filter((policy) => policy.enabled) ?? [], [policies])

  const onEditClick = (): void => {
    navigate({ to: '/compliance/policy' })
  }

  const onPolicyClick = ({ policyId }: WithCompliancePolicyId): void => {
    navigate({ to: `/compliance/violations/${policyId}` })
  }

  const onViolationClick = ({ violationId }: WithComplianceViolationId): void => {
    navigate({ to: `/compliance/violation/${violationId}` })
  }

  const onPoliciesClick = (): void => {
    navigate({ to: '/compliance/policy', search: { action: 'view' } })
  }

  const onViolationsClick = (): void => {
    navigate({ to: '/compliance/violations' })
  }

  const onUserSelect = (userId?: UserId): void => {
    if (userId) navigate({ to: `/compliance/violations/${userId}` })
  }

  return (
    <Page>
      <Page.Heading title={t('Compliance Dashboard')} isFetching={isFetching} refetch={refetch}>
        <UserInlineSelect
          allowDeselect
          value={undefined}
          placeholder='View violations by user...'
          onChange={onUserSelect}
        />
        <TButton variant='secondary' icon='settings' onClick={onEditClick}>
          {t('Edit Policies')}
        </TButton>
      </Page.Heading>
      <Page.Content className='overflow-y-visible'>
        <Grid>
          <Grid.Col span={6}>
            <ComplianceGauge number={agg?.day}>Violations in the last 24 hours</ComplianceGauge>
          </Grid.Col>
          <Grid.Col span={6}>
            <ComplianceGauge number={agg?.week}>Violations in the last 7 days</ComplianceGauge>
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 6, xl: 6 }}>
            <NotReady inline data={activePolicies} error={policiesError} isLoading={isPoliciesLoading} id='Policies'>
              <Paper shadow='xl' radius='xl' p='xl' withBorder>
                <Stack gap='xl'>
                  <Group justify='space-between'>
                    <Text>Active Policies</Text>
                    <TButton variant='ghost' onClick={onPoliciesClick}>
                      View all
                    </TButton>
                  </Group>
                  <CompliancePoliciesList policies={activePolicies} onClick={onPolicyClick} />
                </Stack>
              </Paper>
            </NotReady>
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 6, xl: 6 }}>
            <NotReady inline data={violations} error={violationsError} isLoading={isViolationsLoading} id='Violations'>
              <Paper shadow='xl' radius='xl' p='xl' withBorder>
                <Stack gap='xl'>
                  <Group justify='space-between'>
                    <Text>Latest Violations</Text>
                    <TButton variant='ghost' onClick={onViolationsClick}>
                      View all
                    </TButton>
                  </Group>
                  {violations ? (
                    <ComplianceViolationsList small violations={violations} onClick={onViolationClick} />
                  ) : null}
                </Stack>
              </Paper>
            </NotReady>
          </Grid.Col>
        </Grid>
      </Page.Content>
    </Page>
  )
}

export const Route = createFileRoute('/compliance/')({
  component: ComplianceDashboard,
})
